import { useState, useEffect } from 'react';
import AutocompleteInput, { Option } from '@/components/inputs/AutocompleteInput';
import { useTypesOptions } from '../hooks';
import { AddTypeModal } from './index';
import { GlobalButton } from '@/components';
import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';
import { Add as AddIcon } from '@mui/icons-material';
import { ROLES } from '@/components/security/AppRoles';
import { useUserInfo } from '@/hooks';

interface TypeAutocompleteInputProps {
  name: string;
  placeholder: string;
  value?: Option | null;
  onChange: (value: Option | null) => void;
  onInputChange: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  errorInfo?: string;
  autoFocus?: boolean;
  showCreateButton?: boolean;
  minWidth?: string;
}

const TypeAutocompleteInput = ({
  name,
  placeholder,
  value,
  onChange,
  onInputChange,
  required = false,
  disabled = false,
  error = false,
  errorInfo = '',
  autoFocus = false,
  showCreateButton = false,
  minWidth = 'min-w-[250px]',
}: TypeAutocompleteInputProps) => {
  const [typeSearch, setTypeSearch] = useState('');
  const [showAddTypeModal, setShowAddTypeModal] = useState(false);
  const { permissions } = useUserInfo();

  const { typeOptions, isLoading } = useTypesOptions({
    search: typeSearch,
  });

  const hasPermission = permissions.includes(ROLES.INVENTORY.admin);

  useEffect(() => {
    if (disabled) {
      setTypeSearch('');
    }
  }, [disabled]);

  const handleInputChange = (inputValue: string) => {
    setTypeSearch(inputValue);
    onInputChange(inputValue);
  };

  const handleAddTypeSuccess = (newType: { id: string; name: string }) => {
    const newOption: Option = {
      label: newType.name,
      data: newType.id,
    };
    onChange(newOption);
    setTypeSearch(newType.name);
  };

  return (
    <div className={`flex flex-col gap-2 ${minWidth} w-full`}>
      <div className={minWidth}>
        {typeSearch.length > 0 && typeSearch.length < 3 && (
          <div className='text-gray-500 text-xs absolute -mt-5'>
            Escribe al menos 3 letras para buscar tipos.
          </div>
        )}
        <AutocompleteInput
          name={name}
          placeholder={placeholder}
          value={value}
          inputValue={value ? value.label : typeSearch}
          onChange={onChange}
          onInputChange={handleInputChange}
          options={typeOptions}
          required={required}
          disabled={disabled}
          loading={isLoading}
          error={error}
          errorInfo={errorInfo}
          autoFocus={autoFocus}
        />
      </div>
      {showCreateButton && (
        <div className='w-full # [&>button]:w-full'>
          <GlobalButton
            variant={GlobalButtonVariant.secondaryButton}
            action={() => setShowAddTypeModal(true)}
            disabled={!!value || !hasPermission}
          >
            <AddIcon color='primary' />
            Crear tipo
          </GlobalButton>
        </div>
      )}
      {hasPermission && (
        <AddTypeModal
          open={showAddTypeModal}
          onClose={() => setShowAddTypeModal(false)}
          onSuccess={handleAddTypeSuccess}
        />
      )}
    </div>
  );
};

export default TypeAutocompleteInput;
